import { RadioButtons } from '../../../../widgets/FormikInputFieldV2';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { memoizedOnboardingSelectors } from '../../../../reducers/onboarding';
import { SENSOR_CONFIG_LABEL_MAP } from '../../../../constants';
import onboardingUtils from '../../../../reducers/onboarding/utils';
import { useFormikContext } from 'formik';
import {
  OBSensorConfigurations,
  OptionType,
  StrictSetFieldValue,
} from '../../../../types';
import { Tooltip } from '@rtkwlf/ui';
import Icon from '../../../../widgets/Icons';
import { Label } from 'reactstrap';
import { SensorFormValues } from '../v2/sensor/constants';

/**
 * Builds radio buttons for sensor packet capture config
 * Must be used within a formik context with a field "packetConfig".
 */

type Props = {
  model: string;
};
export const SensorPacketCaptureConfig: React.FunctionComponent<Props> = ({
  model,
}) => {
  const availableHardware = useSelector(
    memoizedOnboardingSelectors.getAvailableHardware,
  );
  const formikContext = useFormikContext<SensorFormValues>();
  const setFieldValue: StrictSetFieldValue<SensorFormValues> =
    formikContext.setFieldValue;

  const supportedConfigs = React.useMemo((): {
    option: OptionType;
    tooltip: React.ReactNode;
  }[] => {
    const currentModelInfo = onboardingUtils.getHardwareInfoByHardwareKey(
      availableHardware.sensors,
      model,
    );
    if (currentModelInfo?.supported_configurations) {
      return currentModelInfo.supported_configurations.map((config) => {
        const optionType = {
          option: {
            label: SENSOR_CONFIG_LABEL_MAP[config],
            value: config,
          },
          tooltip: {},
        };
        if (config === OBSensorConfigurations.SPAN_MIRROR) {
          optionType.tooltip = (
            <div key="mirroring" className="d-flex ">
              <span style={{ width: '250px' }}>
                {SENSOR_CONFIG_LABEL_MAP[config]}
              </span>
              <span>
                A copy of network packets seen on one switch port are sent to
                another switch port where the Arctic Wolf sensor collects and
                analyzes these packets.
              </span>
            </div>
          );
        } else {
          optionType.tooltip = (
            <div key="internalTap" className="d-flex">
              <span style={{ width: '225px' }}>
                {SENSOR_CONFIG_LABEL_MAP[config]}
              </span>
              <span>
                The Arctic Wolf sensor physically goes inline between the
                firewall and the core switch analyzing all network traffic
                traversing the link.
              </span>
            </div>
          );
        }
        return optionType;
      });
    }
    return [];
  }, [availableHardware.sensors, model]);

  React.useEffect(() => {
    if (supportedConfigs.length === 1) {
      setFieldValue(
        'packetConfig',
        supportedConfigs[0].option.value as OBSensorConfigurations,
      );
    }
  }, [supportedConfigs, setFieldValue]);

  if (supportedConfigs.length === 0) {
    return null;
  }
  return (
    <>
      <div className="d-flex">
        <Label className="pb-1 mr-2">Deployment Type</Label>
        <Tooltip
          placement={'right'}
          overlay={
            <div className="p-2" style={{ width: '500px', fontSize: '14px' }}>
              {supportedConfigs.map((opt) => opt.tooltip)}
            </div>
          }
        >
          <Icon.Question />
        </Tooltip>
      </div>
      {supportedConfigs.length === 1 ? (
        <span>{supportedConfigs[0].option.label}</span>
      ) : (
        <RadioButtons
          name="packetConfig"
          options={supportedConfigs.map((opt) => opt.option)}
          className="mb-4"
        />
      )}
    </>
  );
};
